// ============================================================
// TMR Portal Admin — valor copiável ao clique
// ============================================================
import { useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icons, StrokeIcon } from '../lib/icons';

/** Props for {@link CopyableText}. */
export interface CopyableTextProps {
  /** What lands on the clipboard. Also what is rendered, unless `children` says otherwise. */
  value?: string | null;
  /** Rendered instead of `value` — for a masked or decorated version of the same datum. */
  children?: ReactNode;
  /** Shown in place of the value when there is nothing to copy. */
  placeholder?: string;
  /** Extra styling for the value itself (font size, colour). */
  style?: CSSProperties;
}

/**
 * A value the operator can lift with one click.
 *
 * Built for list cells, where the row itself is usually clickable: the click is stopped here so
 * copying never navigates. A drag that selects text is left alone — the browser's own selection
 * still wins, which is what someone reaching for half of a CNPJ expects.
 */
export function CopyableText({ value, children, placeholder = '—', style }: CopyableTextProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  if (!value) return <span style={{ color: 'var(--stone-400)', ...style }}>{placeholder}</span>;

  const copy = () => {
    // A click that ends a text selection is the operator picking part of the value by hand.
    if (!window.getSelection()?.isCollapsed) return;
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <span
      role="button"
      tabIndex={0}
      className="pa-copyable"
      title={copied ? 'Copiado' : `Copiar ${value}`}
      onClick={(e) => {
        e.stopPropagation();
        copy();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        copy();
      }}
      style={style}
    >
      <span>{children ?? value}</span>
      <span className="pa-copyable-icon" aria-hidden="true">
        <StrokeIcon d={copied ? Icons.check : Icons.copy} size={13} />
      </span>
    </span>
  );
}
